import React, { useState, useEffect } from "react";
import "./totalhours.css";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";

const TotalHours = ({ user }) => {
  const [totalHoursData, setTotalHoursData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUserHours = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/gethrTotalHours");
      const data = await response.json();
      
      // Endpoint now returns { totalHours, overtimeHours } per email.
      // totalHours already caps un-approved overtime at 9h/day.
      const userEntry = data[user.email] || {};
      setTotalHoursData({
        email: user.email,
        totalHours: userEntry.totalHours || 0,
        overtimeHours: userEntry.overtimeHours || 0
      });
    } catch (error) {
      console.error("Error fetching total hours:", error);
      setError("Failed to load hours data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchUserHours();
    }
  }, [user?.email]);

  const formatHours = (hours) => {
    if (typeof hours !== 'number' || isNaN(hours)) {
      return "0.00";
    }
    return hours.toFixed(2);
  };

  const getCurrentMonth = () => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[new Date().getMonth()];
  };

  const getHoursStatus = (hours) => {
    if (hours >= 180) return { text: "Complete", color: "#4CAF50" };
    if (hours >= 120) return { text: "Partial", color: "#FF9800" };
    return { text: "Incomplete", color: "#F44336" };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!totalHoursData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <Typography color="textSecondary">No hours data available</Typography>
      </Box>
    );
  }

  const status = getHoursStatus(totalHoursData.totalHours);

  return (
    <div className="total-hours-container">
      <Typography variant="h6" component="h3" gutterBottom className="hours-title">
        Your Total Hours (FMO {getCurrentMonth()})
      </Typography>
      
      <Paper elevation={3} className="hours-card">
        <Box p={2}>
          <div className="hours-display">
            <Typography variant="h4" component="div" className="hours-number">
              {formatHours(totalHoursData.totalHours)}
            </Typography>
            <Typography variant="body2" color="textSecondary" className="hours-label">
              Hours
            </Typography>
          </div>
          
          <div className="hours-status">
            <Typography 
              variant="body2" 
              style={{ color: status.color, fontWeight: 'bold' }}
              className="status-text"
            >
              {status.text}
            </Typography>
          </div>
          
          <div className="hours-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${Math.min((totalHoursData.totalHours / 180) * 100, 100)}%`,
                  backgroundColor: status.color 
                }}
              ></div>
            </div>
            <Typography variant="caption" color="textSecondary" className="target-text">
              Target: 180 hours
            </Typography>
          </div>
        </Box>
      </Paper>
    </div>
  );
};

export default TotalHours;
